import type { TranslationKey } from '../../i18n'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE SCREENSHOT MANIFEST — THE ONLY FILE TO TOUCH WHEN THE IMAGES ARRIVE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The client's reference document (`Docs/From Client/Nettside - Example.pdf`)
 * illustrates almost every homepage section with a phone mockup. We have
 * exactly ONE real screenshot — `public/app-discover.webp`, cropped from the
 * client's own customer plan — and the developer is capturing the rest.
 *
 * So every illustrated section renders `<AppShot id="…" />`, which draws a
 * device frame containing either the real file or a deliberately plain
 * "screenshot coming" panel of the same size. The page does not change shape
 * when a file lands.
 *
 * ─── HOW TO DELIVER A SCREENSHOT ───────────────────────────────────────────
 *
 *   1. Capture the screen described in `shows` below.
 *   2. Export it as the SCREEN ONLY — no device frame, no shadow, no
 *      simulator chrome. The body, the bezel, the Dynamic Island and the glow
 *      are all CSS (`AppShot.tsx`), which stays sharp at any density and
 *      follows the theme.
 *   3. Ratio 2.17, because that is an iPhone Pro Max screen: 440 × 956 pt.
 *      A capture off a real handset already is this and keeps its own status
 *      bar — set `deviceChrome: true` on the row, or the frame draws a SECOND
 *      one and the phone has two clocks. A capture from
 *      `scripts/shoot-app.mjs` is 440 × 881, the content between the strips,
 *      and gets them drawn for it.
 *   4. Save it into `apps/web/public/` under EXACTLY the `file` name below.
 *   5. Change that row's `file` from `null` to the filename, and set
 *      `deviceChrome` if step 3 applies. Nothing else.
 *
 * No component edit, no layout change, no new import, no new string: the alt
 * text is generated from `labelKey`, and `altKey` only exists for the one shot
 * that already had a richer description written for it.
 *
 * ⚠️ `shows` IS NOT A USER-FACING STRING AND IS NEVER RENDERED. It is the
 * capture brief for whoever takes the screenshot, in English, deliberately kept
 * next to the filename so the two cannot drift apart. Hard rule 5 does not
 * apply to it; if you ever find yourself putting it on screen, it needs an i18n
 * key first.
 */

export type AppShotSpec = {
  /** Slot id. Referenced by `<AppShot id="…" />` and by `data.ts`. */
  readonly id: string
  /**
   * Filename inside `apps/web/public/`, or `null` while the screenshot is
   * outstanding. This is the switch — see the header.
   */
  readonly file: string | null
  /**
   * The same screen captured with the app in Bokmål. Optional, and `file` is
   * used when it is absent.
   *
   * ⚠️ IT IS NOT A NICETY HERE, AND THE ENGLISH SHOT IS THE ODD ONE OUT.
   * The seeded content is Norwegian — "EU-kontroll 690 kr", "Elektriker på
   * østkanten" — so a screenshot taken with the app in English shows English
   * chrome wrapped around Norwegian copy. That mixture reads as unfinished in
   * either language. A visitor whose browser is Norwegian gets `nb` (see
   * `src/i18n.ts`, detection order `localStorage → navigator`), which is every
   * visitor the client actually cares about, and the client themselves.
   */
  readonly fileNb?: string
  /**
   * TRUE WHEN THE FILE IS A WHOLE-PHONE CAPTURE THAT ALREADY CONTAINS THE
   * STATUS BAR — i.e. it came off a real handset rather than out of
   * `scripts/shoot-app.mjs`.
   *
   * ⚠️ GET THIS WRONG AND THE PHONE HAS TWO CLOCKS. `AppShot` draws a status
   * bar and a home indicator in CSS, because headless Chrome renders neither
   * and a bare web page inside a phone outline does not read as an app. A
   * screenshot taken on a phone brings its own, and the two stack: the first
   * render of this homepage showed 9:41 in the bezel and 10:51 in the picture,
   * an inch apart.
   *
   * It also settles the geometry, which is the same fact seen from the other
   * side. A Pro Max screen is 440 × 956 pt. A device capture IS that, ratio
   * 2.17. A script capture is the content between the two strips, 440 × 881,
   * ratio 2.00 — and only reaches 2.17 once `AppShot` puts the strips back.
   * So both kinds end up the same shape on the page, by two different routes,
   * and this flag is which route a given file takes.
   */
  readonly deviceChrome?: true
  /** Short section name, shown inside the placeholder so the slot is obvious. */
  readonly labelKey: TranslationKey
  /** Optional richer alt text. Omitted slots fall back to `shot.alt`. */
  readonly altKey?: TranslationKey
  /** Capture brief. Developer-facing, never rendered — see the header. */
  readonly shows: string
}

export const appShots = [
  {
    id: 'request',
    file: 'app-request.png',
    fileNb: 'app-request-nb.png',
    labelKey: 'home.shot.labels.request',
    shows:
      'The request composer, part-way through: the job title filled in, the small job / project / tender choice visible, and the timing, budget, images and preferences fields below it.',
  },
  {
    /*
     * Delivered 29 Sep by `scripts/shoot-app.mjs`, which drives the hosted web
     * build. Every shot from here on is captured that way: repeatable, and not
     * twenty minutes of a person's time each time a screen changes.
     */
    id: 'businesses',
    file: 'app-businesses.png',
    fileNb: 'app-businesses-nb.png',
    labelKey: 'home.shot.labels.businesses',
    shows:
      'The business search results list — several business cards with logo, name, category, rating and distance, and the filter row above them.',
  },
  {
    /*
     * ✅ THE FILTER FILTERS AS OF 12 OCT. STILL EMPTY — NOBODY HAS SHOT IT.
     *
     * ⚠️ SO THIS SLOT HAS CHANGED CATEGORY, exactly as `showcaseBooking` below
     * did on the same day: it was empty because the feature did not work, and it
     * is now empty because the capture has not been run. It belongs with
     * Marketing, not with the screens that do not exist.
     *
     * What it said until 12 Oct, kept verbatim because the complaint is what got
     * the thing built, and because HALF OF IT WAS NEVER TRUE:
     *
     *   ~~`toSearchParams()` in the mobile app drops `availability` on the
     *   floor, deliberately and with a comment: THERE IS NO AVAILABILITY TABLE,
     *   so there is nothing to filter on. Turning the chip on leaves the results
     *   unchanged — two attempts came back byte-identical to the `businesses`
     *   capture, which is now an error the shoot script raises rather than a
     *   duplicate that reaches this file.~~
     *
     * The byte-identical captures were real and the diagnosis was right: the
     * chip was settable and silently did nothing, which this repository's own
     * note on `requests.bidding_enabled` calls worse than a missing control.
     *
     * 🔴 BUT "THERE IS NO AVAILABILITY TABLE" WAS FALSE WHEN IT WAS WRITTEN.
     * `public.opening_hours` has existed with RLS, both policies, two indexes
     * and three CHECK constraints since migration 005, `availability_overrides`
     * sits beside it, and `02-demo-businesses.mjs` has been seeding six weekly
     * patterns all along — the WEBSITE renders them on every business profile.
     * The same sentence was in `lib/filters.ts`, `lib/types.ts` and
     * `AvailabilityPill`'s header, all four agreeing because they were copies of
     * one another. What was actually missing was a predicate a paginated query
     * could apply, which is now four PostgREST computed columns in
     * `supabase/migrations/20261012300000_business_opening_hours.sql`.
     *
     * 🔴 TO SHOOT IT, RUN THE SEED FIRST, AND USE A 393 pt WIDTH.
     *
     * 1. `node supabase/seed/10-availability.mjs`. Its dates are relative to the
     *    run, so a seed from last week is a vacation in the past and the chips
     *    stop distinguishing anything. That file's header says so.
     * 2. `scripts/shoot-app.mjs` renders at **440 pt and nothing else**, which
     *    CLAUDE.md records as the reason every picture ever taken of this app is
     *    of the one width that works. A 393 pt capture is the honest one.
     * 3. The shot wants "This week" selected, NOT "Now": "now" is five venues at
     *    18:00 and could be none at 03:00, so it is the one option whose
     *    screenshot depends on the hour it was taken.
     *
     * The shot is written and disabled in `scripts/shoot-app.mjs`; enable it
     * with `--only availability`.
     *
     * ✅ FILLED 13 OCT 2026, from the HOSTED build, in both languages —
     * `node scripts/shoot-app.mjs --lang nb --only availability` and the same
     * with `--lang en`.
     *
     * ⚠️ IT COULD NOT HAVE BEEN SHOT BEFORE TODAY, AND NOT FOR WANT OF TRYING.
     * The filter needs opening-hours data to have something to filter ON, and
     * `business_opening_hours` (migration `20261012300000`) only reached `main`
     * when `wip/session-38-mobile` merged this morning. The hosted preview was
     * then republished the same day — verified by downloading its bundle and
     * finding `business_availability` and `business_open_now` in it — so this is
     * a capture of the real deployed app rather than of a local export, which is
     * the caveat the `discover` row below still carries.
     *
     * ⚠️ THE HOUR IT WAS TAKEN IS STILL PART OF THE PICTURE, exactly as the note
     * above warns: "available today" is a different set at 18:00 than at 03:00.
     * If this is ever re-shot and the marker is missing from every card, check
     * the clock before checking the query.
     *
     * NO `deviceChrome`: both files are 1320 × 2643, i.e. 440 × 881 at scale 3 —
     * the content area between the two strips, which `AppShot` then draws. A
     * device capture would be ratio 2.17; these are 2.00.
     */
    id: 'availability',
    file: 'app-availability.png',
    fileNb: 'app-availability-nb.png',
    labelKey: 'home.shot.labels.availability',
    shows:
      'The same search results, but with the availability filter switched on and the "available this week" marker visible on the cards.',
  },
  {
    /*
     * ✅ FILLED 12 OCT 2026. The client's § 5.3 condition is met and this is the
     * capture that meets it — `scripts/shoot-app.mjs --lang nb --only discover`.
     *
     * He held it back on 29 Sep: *"The same approach should be used for the
     * Discover page until its UI is finished. Once the actual UI is completed,
     * the placeholder screenshots should be replaced with screenshots showing
     * how the app actually looks."* Discover has had no fixtures since the
     * evening of 6 Oct — Daily, Deals and Featured are all real queries — and
     * his 12 Oct review then changed the screen again.
     *
     * ⚠️ THE THREE OLD DISCOVER FILES ARE SUPERSEDED AND MUST NOT COME BACK.
     * `app-discover.jpeg`, `app-discover-nb.jpeg` (handset captures of the
     * FIXTURE version — the thing he objected to) and the pre-23-Sep
     * `app-discover.webp` (whose lower half is the retired community feed) are
     * all still in `apps/web/public/` and all three are dead. This row points at
     * `app-discover.png`, which is the new one.
     *
     * ⚠️ IT WAS NOT SHOT FROM THE HOSTED PREVIEW, AND IT COULD NOT HAVE BEEN.
     * <https://avexa-marketplace.expo.app> is a **9 Oct** build (`Last-Modified`
     * on its `index.html`), so it still has the Deals RAIL on Discover — which
     * § 5(c) of the 12 Oct review removed, and which is already gone from
     * `main`. A capture from there would have published a band the client had
     * asked to delete three days earlier. This is a local `expo export
     * --platform web` of the current tree instead, served statically and driven
     * through `--url`. **Re-shoot from the hosted build once the preview is
     * republished**, and the picture should come out the same.
     *
     * ⚠️ BOKMÅL ONLY, DELIBERATELY, SO `file` CARRIES THE NORWEGIAN CAPTURE —
     * the same choice `deals` makes two rows down. The English run succeeds and
     * was looked at: it wraps English chrome ("Question of the Day", "33
     * answered today") around a poll card that is entirely Norwegian — "Hva ser
     * du først etter når du velger håndverker? · Pris · Anmeldelser" — and that
     * card is half the frame. Fully-Norwegian chrome reads better than that
     * mixture.
     *
     * NO `deviceChrome`: this is a script capture, 440 × 881, the content area
     * between the two strips that `AppShot` draws.
     */
    id: 'discover',
    file: 'app-discover.png',
    labelKey: 'home.shot.labels.discover',
    altKey: 'home.shot.discoverAlt',
    shows:
      'Discover as it stands after the 12 Oct review: the Deals and Featured shortcut buttons, the Daily carousel showing the question of the day with live vote percentages, and the Featured band of articles from local businesses underneath. The Deals rail is gone from this screen — § 5(c) took it out.',
  },
  {
    /*
     * Delivered 29 Sep. The DEAL DETAIL screen rather than a card in the rail,
     * because the brief asks for the price pair, the percentage AND the
     * countdown to be legible, and at the width this slot renders a rail card's
     * struck-through price is about four pixels tall.
     */
    id: 'deals',
    file: 'app-deals.jpeg',
    deviceChrome: true,
    labelKey: 'home.shot.labels.deals',
    shows:
      'A deal card as the app draws it: the new price, the struck-through original price, the discount percentage and the validity countdown, with the business that published it named above.',
  },
  {
    /*
     * The COMPARISON screen, not the request detail. The detail page shows the
     * job; the brief asks for the offers side by side, and that is one tap
     * further on — "See offers".
     */
    id: 'requests',
    file: 'app-requests.png',
    fileNb: 'app-requests-nb.png',
    labelKey: 'home.shot.labels.requests',
    shows:
      'One request with several offers under it, so the comparison is the subject: price, timing and the business behind each offer, side by side.',
  },
  {
    /*
     * ⚠️ Any capture of this screen taken before 29 Sep shows **NOK 0**.
     * `offer_totals()` sums line items and coalesces to 0, and the fallback to
     * `price_amount` was written with `??`, which 0 does not trigger. Fixed in
     * `MessageListItem.tsx`; this one reads 1 890 kr, which is the real offer.
     */
    id: 'messages',
    file: 'app-messages.png',
    fileNb: 'app-messages-nb.png',
    labelKey: 'home.shot.labels.messages',
    shows:
      'A conversation that carries an offer card and an appointment inside the thread, with the request it belongs to named in the header.',
  },
  {
    /*
     * Delivered 29 Sep, and it needed a seed before it could be taken: the
     * business every demo login owns had no matched requests at all, so this
     * screen was its empty state. Two leads now, one published today.
     */
    id: 'opportunities',
    file: 'app-opportunities.jpeg',
    fileNb: 'app-opportunities-nb.jpeg',
    deviceChrome: true,
    labelKey: 'home.shot.labels.opportunities',
    shows:
      'The business side: the Opportunities inbox, a list of open requests matched to the business, each one showing category, area and how long it has been open.',
  },
  {
    /*
     * ⚠️ Any capture before 29 Sep is topped by the word "business" in lower
     * case — the parent stack was titling a nested navigator by its route
     * segment. Fixed in `app/(shared)/_layout.tsx`.
     */
    id: 'showcaseProfile',
    file: 'app-showcase-profile.png',
    fileNb: 'app-showcase-profile-nb.png',
    labelKey: 'home.shot.labels.showcaseProfile',
    shows:
      'A business profile at the top of the screen: cover, logo, name, category, rating summary and the contact actions.',
  },
  {
    /*
     * ✅ THE SCREEN EXISTS AS OF 12 OCT. STILL EMPTY — IT HAS NOT BEEN SHOT.
     *
     * ⚠️ THIS SLOT HAS CHANGED CATEGORY AND THE DISTINCTION MATTERS, because
     * `showcaseMarketing` below draws exactly that line: `availability` is
     * empty because the screen does not work; this one is now empty because
     * nobody has run the capture. It belongs with Marketing, not with
     * availability.
     *
     * What it said until 12 Oct, kept because it is the complaint the screen
     * was built from:
     *
     *   ~~The brief asks for "a calendar with a day selected and the available
     *   time slots under it". `services.is_bookable` is rendered as a badge and
     *   nothing is behind it; `app/(shared)/booking/[id].tsx` shows a booking
     *   that already exists. There is no create-a-booking flow to photograph.~~
     *
     * It is `/(shared)/booking/new?serviceId=<uuid>` —
     * `apps/mobile/src/features/bookings/` — reached from the ✓ Bookable badge
     * on a business profile's price list, which is what had nothing behind it.
     * Day strip, time grid, confirm sheet, a real `bookings` row through
     * `book_slot()` (migration 110), and the business is notified.
     *
     * 🔴 TWO THINGS TO GET RIGHT WHEN SHOOTING IT, AND THE FIRST IS A TRAP:
     *
     * 1. `scripts/shoot-app.mjs` renders at **440 pt and nothing else**, which
     *    is why every picture ever taken of this app is of the one width that
     *    works. The slot grid is three fixed columns computed from the window
     *    width, so 440 will look right and say nothing about 393 — iPhone
     *    14 Pro / 15 / 15 Pro / 16. Shoot it at 393.
     * 2. A day must be SELECTED and the grid must have slots in it, which means
     *    the capture has to land on a weekday the demo business is open. Salong
     *    Nord's 30-minute haircut had 369 slots over a fortnight when this was
     *    written, and zero on the Sunday — a Sunday capture is a legitimately
     *    empty state and the wrong picture for this slot.
     */
    /*
     * ✅ FILLED 13 OCT 2026, both languages, from the HOSTED build —
     * `node scripts/shoot-app.mjs --lang nb --only showcaseBooking`.
     *
     * ⚠️ IT WAS GENUINELY UNSHOOTABLE BEFORE TODAY, which is why the slot sat
     * empty rather than being forgotten. There was no create-a-booking flow at
     * all until migration `20261012310000`, and nothing to put in a calendar
     * until `opening_hours` had rows; both reached `main` this morning and the
     * hosted preview was republished the same day.
     *
     * ⚠️ THE SHOT IS NOT `/jobs` → Bookings. That is the list of bookings
     * already made — an empty state, and the wrong picture for a slot whose
     * brief asks for the calendar. It walks the profile instead:
     * Storo Bowling → Services tab → Book, which is the path a customer takes.
     *
     * ⚠️ `storo-bowling-og-biljard` was chosen BY QUERYING, not by taste: it has
     * seven open days in `opening_hours`, so there is no weekday on which this
     * capture comes out as an empty grid. A business shut on the day the shot
     * runs would produce a correct and useless picture.
     *
     * NO `deviceChrome`: 1320 × 2643, i.e. 440 × 881 at scale 3 — the content
     * area, which `AppShot` then draws the strips around.
     */
    id: 'showcaseBooking',
    file: 'app-showcase-booking.png',
    fileNb: 'app-showcase-booking-nb.png',
    labelKey: 'home.shot.labels.showcaseBooking',
    shows:
      'The booking flow: a calendar with a day selected and the available time slots under it.',
  },
  {
    /*
     * Delivered 29 Sep — the business Hub, which is where these figures live.
     *
     * ⚠️ It does NOT show search appearances or profile views, and that is not
     * an oversight in the capture: `response_rate`, `median_response_minutes`
     * and the 7-day profile-view count are columns nothing computes yet, so the
     * app renders an em dash for each (Docs/15). The tiles that ARE real —
     * leads, unread, bookings, matched, won, completed — are the ones in frame.
     * When the missing aggregates land, re-shoot and update `shows` with them.
     */
    id: 'showcaseAnalytics',
    file: 'app-showcase-analytics.jpeg',
    fileNb: 'app-showcase-analytics-nb.jpeg',
    deviceChrome: true,
    labelKey: 'home.shot.labels.showcaseAnalytics',
    shows:
      'The business Hub: new leads, unread messages, upcoming bookings, and the requests-and-jobs tally — matched, offers won, jobs completed.',
  },
  {
    /*
     * ⚠️ THE FOURTH BUSINESS SCREEN, ASKED FOR BY NAME. § 6:
     * *"The website should also show a screenshot from the marketing/Business
     * Mode section. Because there are currently three screenshots at the same
     * place of the business-app in the Home-page area. Adding another screenshot
     * would create four, which should also look better on desktop."*
     *
     * He is describing `ShowcaseSection`, whose three slots are all business
     * screens — profile, booking, analytics — and which laid out 3-across on a
     * desktop. It is now four, and the grid is 4-across from `lg` (2 × 2 at
     * `sm`), which is the layout half of what he asked for.
     *
     * ✅ CAPTURED 12 OCT 2026 — `scripts/shoot-app.mjs --only showcaseMarketing`,
     * both languages. The screen is `(business)/(tabs)/content`, "Content and
     * marketing" / "Innhold og markedsføring", which is the marketing surface
     * the app actually has.
     *
     * ⚠️ NOT FROM THE HOSTED PREVIEW, AND THE DIFFERENCE IS THE WHOLE SUBJECT.
     * <https://avexa-marketplace.expo.app> is a 9 Oct build, and on 9 Oct this
     * screen was a pure READ — its own empty state said *"there is no business
     * composer in the app yet"*. § 10.2a of the 12 Oct review (*"It is currently
     * not possible to publish or edit a Deal"*) added the composer, and the
     * composer is the thing worth photographing. So this is a local
     * `expo export --platform web` of the current tree, served statically and
     * driven through `--url`. Re-shoot from the hosted build once the preview is
     * republished.
     *
     * ⚠️ NO PLATFORM PRICE IS IN FRAME, WHICH IS THE RULE THIS SLOT CARRIES.
     * The client's own payment module gives two contradicting Boost price lists,
     * two Avexa+ prices and both two and three sponsored slots per category
     * (Docs/15 N20–N24, Q143), so a screenshot is the cheapest possible way to
     * publish a number nobody has decided. That is why this is the Content tab
     * and NOT `BoostPanel`, which is the other honest answer to "how a business
     * promotes itself" and which renders the NOK 299 package and its VAT split.
     * ⚠️ A business's OWN deal price is a different thing and is in frame —
     * "EU-kontroll 690 kr" is Drammen Bilverksted's campaign, not our tariff,
     * and the delivered `deals` shot has shown one since 29 Sep.
     *
     * ⚠️ IT IS SPARSE, AND THAT IS DATA RATHER THAN LAYOUT. The demo account
     * the script signs in as (Tor → Drammen Bilverksted) has exactly ONE
     * published post, so the list is a heading, the composer button and one
     * card over about half a screen of white. Checked at the size this actually
     * renders — ~250 px wide in `ShowcaseSection`'s 4-up grid — where it reads
     * as a clean app screen rather than a broken one. The fix is seed data, not
     * a re-crop: give that business two or three more campaigns, all in
     * Norwegian, and re-run. The other two business accounts in `DemoLogins.tsx`
     * are no better — Silje's company has two posts and one of them is in
     * English.
     *
     * NO `deviceChrome`: script capture, 440 × 881.
     */
    id: 'showcaseMarketing',
    file: 'app-showcase-marketing.png',
    fileNb: 'app-showcase-marketing-nb.png',
    labelKey: 'home.shot.labels.showcaseMarketing',
    shows:
      'Business Mode → Content and marketing: the business’s own published campaigns with their status, the Edit control on each, and the "New deal" composer button above them. ⚠️ NO PLATFORM PRICE IN FRAME — no Boost, Avexa+ or sponsored-slot figure. See the comment above.',
  },
] as const satisfies readonly AppShotSpec[]

export type AppShotId = (typeof appShots)[number]['id']

/** How many slots are still waiting for a file. Used by nothing but a grep. */
export const outstandingShots = (): number => appShots.filter((shot) => shot.file === null).length
